<template>
  <div ref="container" v-on:click="closeCalendar" class="container">
    <div class="form-container">
      <BudgetPeriodPicker
        :start="
          getInitialBudgetPeriod({
            startDate: form.startTime,
            endDate: form.endTime,
          }).startDate
        "
        :end="
          getInitialBudgetPeriod({
            startDate: form.startTime,
            endDate: form.endTime,
          }).endDate
        "
        :dummyStartDateForInitialMonth="dummyStartDateForInitialMonth"
        :dummyEndDateForInitialMonth="dummyEndDateForInitialMonth"
        :usable-start-date-filter="usableStartDateFilter"
        :usable-end-date-filter="usableEndDateFilter"
        :occupiedPeriods="
          getOccupiedPeriodsForEditBudget(
            budgetList,
            (() => {
              if (isRealBudget) return budgetIdOrRequestId
              return undefined
            })(),
          )
        "
        :changeIsOpenCalendar="type => changeIsOpenCalendar(type)"
        :isOpenStartCalendar="isOpenStartCalendar"
        :isOpenEndCalendar="isOpenEndCalendar"
        @inputStartDate="date => onInputStartDate(date)"
        @inputEndDate="date => onInputEndDate(date)"
        :is-new="!isRealBudget"
      />
      <div class="budget-currency">
        <v-text-field
          :value="form.budgetAmount"
          v-on:input="value => onInputBudget(value)"
          v-on:blur="
            () => {
              if (form.budgetAmount) {
                form.budgetAmount = form.budgetAmount.replace(/[^0-9]/g, '')
              }
            }
          "
          @keydown.arrow-down.prevent
          @keydown.arrow-up.prevent
          @wheel.prevent
          label="予算（JPY）"
          placeholder="500,000"
          type=""
        />
      </div>
    </div>
    <div v-if="showValidationError">
      <div v-for="error in validationErrors" class="validation-error">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
dayjs.extend(isBetween)
import {
  actions as snackbarActions,
  snackbarType,
} from '@/vuex/modules/ui/snackbar/constants'
import BudgetRequestApi from '@/api/BudgetRequest'
import {
  getOccupiedPeriodsForEditBudget,
  getValidationErrors,
} from '@/models/BudgetRequest'
import BudgetPeriodPicker from '../Form/BudgetPeriodPicker'

export default {
  name: 'EditBudgetForm',
  components: {
    BudgetPeriodPicker,
  },
  props: {
    insertionOrder: {
      type: Object,
      require: false,
      default: () => {},
    },
    advertiser: {
      type: Object,
      require: false,
      default: () => {},
    },

    // form入力に必要
    budgetIdOrRequestId: {
      type: Number,
      require: true,
      default: 0,
    },
    isRealBudget: {
      type: Boolean,
      require: true,
      default: true,
    },
    startTime: {
      type: String,
      require: true,
      default: '',
    },
    endTime: {
      type: String,
      require: true,
      default: '',
    },
    budgetAmount: {
      type: Number,
      require: true,
      default: 0,
    },

    // occupiedPeriodsを取得するために必要
    budget: {
      type: Object,
      require: false,
      default: () => {},
    },
    budgetList: {
      type: Array,
      required: true,
    },

    // validation周りに必要
    showValidationError: {
      type: Boolean,
      default: false,
    },
    isValid: {
      type: Boolean,
      default: true,
    },
    onChangeShowValidationError: {
      type: Function,
      default: () => {},
    },
    onValidate: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      form: {
        startTime: this.startTime,
        endTime: this.endTime,
        budgetAmount: this.budgetAmount.toString(),
      },
      isOpenStartCalendar: false,
      isOpenEndCalendar: false,
      validationErrors: [],
      // date-pickerにデフォルトで表示される月を指定する機能がないため、それを実現するために一瞬だけ表示するダミーの日付
      dummyStartDateForInitialMonth: undefined,
      dummyEndDateForInitialMonth: undefined,
    }
  },
  computed: {
    currency() {
      return this.advertiser.currency
    },
    confirmationBody() {
      const currency = this.currency
      const { formatCurrency } = this.$options.filters
      // eslint-disable-next-line
      return `
      <div style="font-size: 0.9rem; margin-top: -50px;">
        <div>
          <table>
            <thead>
              <tr style="font-weight: 400;">
                <th style="width: 75px; text-align: center;">開始日</th>
                <th style="width: 10px;"></th>
                <th style="width: 75px; text-align: center;">終了日</th>
                <th style="width: 75px; text-align: right;">予算</th>
              </tr>
            </thead>
            <tbody>
                    <tr>
                      <td style="padding-top: 5px; text-align: center;">
                        ${dayjs(this.form.startTime).format('YYYY/M/D')}
                      </td>
                      <td style="padding-top: 5px; text-align: center;"> 〜 </td>
                      <td style="padding-top: 5px; text-align: center;">
                        ${dayjs(this.form.endTime).format('YYYY/M/D')}
                      </td>
                      <td style="text-align: right; padding-top: 5px;">${formatCurrency(
                        this.form.budgetAmount,
                        currency,
                      )}
                      </td>
                    </tr>
            </tbody>
          </table>
        </div>
        案件予算のご申請をいただく場合、当社の定める利用規約にご同意の上、
        保存ボタンを押して予算申請をお願いします。
        <a href="https://www.geologic.co.jp/tos" target="_blank">利用規約を確認する</a>
        </div>
        `
    },
    confirmationMessage() {
      if (this.isRealBudget) {
        return '案件予算を変更申請します'
      } else {
        return '案件予算を再申請します'
      }
    },
    /**
     * 既に申請予定、申請中、承認済みの予算が持つ期間かどうか判定するメソッドを返す
     */
    usableStartDateFilter() {
      if (this.isRealBudget) return () => true
      // dateを渡すとt/fを返す関数を返す
      return date =>
        this.budgetList
          .filter(budget =>
            ['new', 'waiting', 'approved'].includes(budget.requestStatus),
          )
          .every(
            budget =>
              dayjs(budget.summary.startTime).isAfter(date) ||
              dayjs(budget.summary.endTime).isBefore(date),
          )
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        this.validate()
      },
    },
  },
  methods: {
    validate() {
      const validationErrors = getValidationErrors({
        startTime: this.form.startTime,
        endTime: this.form.endTime,
        budget: this.form.budgetAmount,
      })
      const isValid = validationErrors.length === 0 ? true : false
      this.validationErrors = validationErrors
      this.$emit('onValidate', isValid)
    },
    onInputStartDate(date) {
      if (date === undefined) {
        this.form.startTime = undefined
        return
      }
      this.form.startTime = `${date} 00:00:00`
      this.isOpenStartCalendar = false
    },
    onInputEndDate(date) {
      if (date === undefined) {
        this.form.endTime = undefined
        return
      }
      this.form.endTime = `${date} 23:59:59`
      this.isOpenEndCalendar = false
    },
    onInputBudget(value) {
      this.form.budgetAmount = value.replaceAll(',', '')
    },
    getOccupiedPeriodsForEditBudget(budgetList, budgetId) {
      return getOccupiedPeriodsForEditBudget(budgetList, budgetId)
    },
    getInitialBudgetPeriod({ startDate, endDate }) {
      // 変更申請の場合は、そのまま開始日、終了日を返す
      if (this.isRealBudget) return { startDate: startDate, endDate: endDate }
      // 開始日が今日以前もしくは、既に設定されている日付以内の場合undefinedで返す
      const validStartDate = (startDate => {
        if (
          dayjs(
            dayjs()
              .subtract(1, 'd')
              .format('YYYY-MM-DD 23:59:59'),
          ).isAfter(startDate)
        )
          return undefined
        if (!this.usableStartDateFilter(startDate)) return undefined
        return startDate
      })(startDate)
      // 終了日が今日以前もしくは、既に設定されている日付以内の場合undefinedで定義
      const validEndDate = ((startDate, endDate) => {
        if (dayjs().isAfter(endDate)) return undefined
        if (!this.usableStartDateFilter(endDate)) return undefined
        return endDate
      })(startDate, endDate)
      // 終了日・開始日がundefinedの場合、validした内容で返す
      if (validStartDate === undefined || validEndDate === undefined)
        return { startDate: validStartDate, endDate: validEndDate }
      // 期間内に内包している予算があるかチェックをしてあれば、endDateのみundefinedにして返す
      if (this.checkOverlapped(startDate, endDate))
        return { startDate: startDate, endDate: undefined }
      // 問題ない場合は、申請内容に従って返す
      return { startDate: startDate, endDate: endDate }
    },
    checkOverlapped(startDate, endDate) {
      const occupiedPeriods = this.getOccupiedPeriodsForEditBudget(
        this.budgetList,
        (() => {
          if (this.isRealBudget) return this.budgetIdOrRequestId
          return undefined
        })(),
      )
      return (
        occupiedPeriods.filter(checkPeriod => {
          return dayjs(checkPeriod.start).isBetween(
            startDate,
            endDate,
            null,
            '[]',
          )
        }).length > 0
      )
    },
    usableEndDateFilter(date, startDate) {
      var filteredBudgetList = []
      // 変更申請は常に true
      if (!this.budget.isNew) {
        return true
      }
      filteredBudgetList = this.budgetList.filter(
        budget =>
          ['new', 'waiting', 'approved'].includes(budget.requestStatus) &&
          dayjs(budget.summary.startTime).isAfter(startDate),
      )
      if (!filteredBudgetList.length) {
        return true
      }
      return filteredBudgetList.every(budget =>
        dayjs(budget.summary.startTime).isAfter(date),
      )
    },
    async submit() {
      this.onChangeShowValidationError(true)
      if (!this.isValid) return

      const result = await this.$confirm(this.confirmationMessage, {
        body: this.confirmationBody,
        cancelText: 'キャンセル',
        okText: '保存',
      })
      if (!result) {
        return
      }

      if (this.isRealBudget) {
        // 変更申請の場合
        await BudgetRequestApi.edit({
          insertionOrderId: this.insertionOrder.id,
          insertionOrderBudgetId: this.budgetIdOrRequestId,
          startTime: this.form.startTime,
          endTime: this.form.endTime,
          budget: parseInt(this.form.budgetAmount),
        })
      } else {
        // 再申請の場合
        await BudgetRequestApi.resubmit({
          insertionOrderBudgetRequestId: this.budgetIdOrRequestId,
          startTime: this.form.startTime,
          endTime: this.form.endTime,
          budget: parseInt(this.form.budgetAmount),
        })
        await this.$confirm('', {
          okText: '閉じる',
          onlyOk: true,
          body: `案件予算の申請後、キャンペーンにも改めて予算設定をする必要がございますので、ご注意ください。`,
        })
      }
      this.insertionOrder.fetchBudgetList()
      this.$store.dispatch(snackbarActions.SHOW_SNACKBAR, {
        type: snackbarType.SUCCESS,
        message: '申請しました',
      })
      this.$emit('success')
    },
    getDisplayBudget(budget) {
      if (typeof budget === 'number') return budget.toLocaleString()

      const parsedBudget = parseInt(
        budget.replace(/[Ａ-Ｚａ-ｚ０-９]/g, s =>
          String.fromCharCode(s.charCodeAt(0) - 0xfee0),
        ),
      )
      return isNaN(parsedBudget) ? budget : parsedBudget.toLocaleString()
    },
    changeIsOpenCalendar(type) {
      if (type === 'start' && !this.isOpenStartCalendar) {
        this.isOpenStartCalendar = true
        this.isOpenEndCalendar = false
      } else if (type === 'end' && !this.isOpenEndCalendar) {
        this.isOpenStartCalendar = false
        this.isOpenEndCalendar = true
      } else {
        this.isOpenStartCalendar = false
        this.isOpenEndCalendar = false
      }
    },
    closeCalendar() {
      this.isOpenEndCalendar = false
      this.isOpenStartCalendar = false
    },
  },
}
</script>

<style scoped lang="scss">
.container {
  overflow-y: scroll;
  max-height: 70vh;
}

.expandButton {
  width: 100%;
  margin: 0;
  padding: 0;

  ::v-deep .v-btn__content {
    justify-content: flex-start;
  }
}

.budget-title {
  padding-right: 30px;
}

.budget-currency {
  width: 50%;
  margin-left: 30px;
}

.form-container {
  display: flex;
  align-items: flex-start;
  margin-right: 30px;
  padding-bottom: 0;
  overflow-y: hidden;
}

.validation-error {
  color: #ff0000;
  padding-left: 40px;
}

.button-add-budget {
  margin-top: 20px;
}

.button-delete {
  margin-top: 20px;
  margin-left: 4px;
}
</style>
